import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'
import {
  sendTeamInviteEmail,
  type TeamInviteEmailParams,
} from '@/lib/notificationWorkerClient'

export interface TeamMember {
  id: string
  organization_id: string
  user_id: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  created_at: string
  user: {
    id: string
    name: string | null
    email: string
    avatar_url: string | null
  }
}

export interface TeamInvitation {
  id: string
  email: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  expires_at: string
  invited_at: string
}

export function useOrganizationMembersQuery(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['team-members', organizationId],
    queryFn: async () => {
      if (!organizationId) return [] as TeamMember[]
      return supabaseRpc<TeamMember[]>('list_team_members', { p_org_id: organizationId })
    },
    enabled: !!organizationId,
  })
}

export function useTeamInvitationsQuery(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['team-invitations', organizationId],
    queryFn: async () => {
      if (!organizationId) return [] as TeamInvitation[]
      return supabaseRpc<TeamInvitation[]>('list_team_invitations', { p_organization_id: organizationId })
    },
    enabled: !!organizationId,
  })
}

export function useInviteTeamMemberMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: TeamInviteEmailParams) => sendTeamInviteEmail(params),
    onSuccess: (_data, params) => {
      void qc.invalidateQueries({ queryKey: ['team-invitations', params.organizationId] })
    },
  })
}

export function useRemoveTeamMemberMutation(organizationId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) => supabaseRpc('remove_team_member', { p_member_id: memberId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team-members', organizationId] })
    },
  })
}

export function useUpdateTeamMemberRoleMutation(organizationId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { id: string; role: 'owner' | 'admin' | 'manager' | 'viewer' }) =>
      supabaseRpc('update_team_member_role', {
        p_member_id: params.id,
        p_role: params.role,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team-members', organizationId] })
    },
  })
}
